import React, { useState, useEffect } from "react";
import {
    Activity,
    User,
    Settings,
    FileText,
    Clock,
    Search
} from "lucide-react";
import { superAdminAPI } from "../../api/apiService";
import { formatDistanceToNow } from "date-fns";

export default function RecentActivityPage() {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchActivities = async () => {
        try {
            setLoading(true);
            const res = await superAdminAPI.getActivityLogs({ limit: 50 });
            setActivities(res.data || []);
        } catch (error) {
            console.error("Error fetching activities", error);
            // Fallback mock data
            setActivities([
                { id: 1, action: "User Login", user: "Admin", details: "Logged in from 192.168.1.1", created_at: new Date().toISOString(), type: "login" },
                { id: 2, action: "Settings Update", user: "Super Admin", details: "Updated tax configuration", created_at: new Date(Date.now() - 3600000).toISOString(), type: "settings" },
            ]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchActivities();
    }, []);

    const getActivityIcon = (type) => {
        switch (type) {
            case 'login': return <User className="w-5 h-5 text-green-500" />;
            case 'settings': return <Settings className="w-5 h-5 text-orange-500" />;
            case 'document': return <FileText className="w-5 h-5 text-blue-500" />;
            default: return <Activity className="w-5 h-5 text-slate-500" />;
        }
    };

    const filteredActivities = activities.filter(activity => {
        const term = searchTerm.toLowerCase();
        const user = String(activity.business_name || activity.user_name || activity.user || "").toLowerCase();
        const action = String(activity.action || "").toLowerCase();
        const description = String(activity.description || activity.details || "").toLowerCase();

        return user.includes(term) || action.includes(term) || description.includes(term);
    });

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Recent Activity</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">Comprehensive log of system activities and user actions</p>
                </div>
                <button
                    onClick={fetchActivities}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition"
                >
                    Refresh Logs
                </button>
            </div>

            {/* Search and Filters */}
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search logs by user, action, or details..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white"
                    />
                </div>
            </div>

            {/* Activity List */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-slate-500">Loading activity logs...</div>
                ) : filteredActivities.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Activity className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">No activities found</h3>
                        <p className="text-slate-500 dark:text-slate-400">Try adjusting your search filters</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                        {filteredActivities.map((activity) => (
                            <div key={activity.id} className="p-5 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group">
                                <div className="flex items-start gap-4">
                                    <div className="mt-1 flex-shrink-0 p-2 bg-slate-100 dark:bg-slate-700 rounded-lg group-hover:bg-white dark:group-hover:bg-slate-600 transition-colors shadow-sm">
                                        {getActivityIcon(activity.action || activity.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                                                        {activity.business_name || activity.user_name || "System"}
                                                    </p>
                                                    {activity.role && (
                                                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                            {activity.role}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                                                    {activity.description || activity.details || "No details provided"}
                                                </p>
                                            </div>
                                            <span className="flex items-center gap-1.5 text-xs text-slate-400 whitespace-nowrap bg-slate-50 dark:bg-slate-700/50 px-2 py-1 rounded-full">
                                                <Clock className="w-3 h-3" />
                                                {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
